/**
 * @engine/core - TransformStorage
 * 
 * Structure of Arrays (SoA) storage for transform data.
 * Optimized for batch operations and cache efficiency.
 * 
 * Layout:
 * - positions: Float32Array [x0,y0,z0, x1,y1,z1, ...]
 * - rotations: Float32Array [r0, r1, ...]
 * - scales: Float32Array [sx0,sy0, sx1,sy1, ...]
 * - parents: Int32Array [-1, 0, 1, ...] (-1 = no parent)
 * - flags: Uint8Array [dirty bits]
 * - worldPositions/Rotations/Scales: cached world transforms
 */

// Dirty flag bits
const DIRTY_LOCAL = 0x01;
const DIRTY_WORLD = 0x02;
const _DIRTY_CHILDREN = 0x04; void _DIRTY_CHILDREN; // For future use

// Safe array access - storage guarantees valid indices
function get<T>(arr: ArrayLike<T>, i: number): T { return arr[i] as T; }

// Position stride (xyz)
const POS_STRIDE = 3;
// Scale stride (xy)
const SCALE_STRIDE = 2;

/**
 * Transform slot handle (for external use)
 */
export type TransformHandle = number;

/**
 * Invalid transform handle
 */
export const INVALID_HANDLE: TransformHandle = -1;

/**
 * TransformStorage configuration
 */
export interface TransformStorageConfig {
  /** Initial capacity */
  initialCapacity?: number;
  /** Grow factor when capacity exceeded */
  growFactor?: number;
}

/**
 * Transform data for bulk operations
 */
export interface TransformData {
  x: number;
  y: number;
  z: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

/**
 * World transform (cached)
 */
export interface WorldTransform {
  x: number;
  y: number;
  z: number;
  rotation: number;
  scaleX: number;
  scaleY: number;
}

/**
 * TransformStorage - SoA storage for transforms
 * 
 * @example
 * ```typescript
 * const storage = new TransformStorage({ initialCapacity: 1000 });
 * 
 * // Allocate transforms
 * const handle = storage.allocate();
 * storage.setPosition(handle, 100, 200, 0);
 * storage.setRotation(handle, Math.PI / 4);
 * 
 * // Batch update world transforms
 * storage.updateWorldTransforms();
 * 
 * // Access world position
 * const world = storage.getWorldTransform(handle);
 * ```
 */
export class TransformStorage {
  // Local transforms (relative to parent)
  private _positions!: Float32Array;    // x,y,z per transform
  private _rotations!: Float32Array;    // radians
  private _scales!: Float32Array;       // sx,sy per transform
  
  // Hierarchy
  private _parents!: Int32Array;        // parent handle (-1 = root)
  private _firstChild!: Int32Array;     // first child handle (-1 = none)
  private _nextSibling!: Int32Array;    // next sibling handle (-1 = none)
  
  // Flags (dirty, active, etc.)
  private _flags!: Uint8Array;
  
  // Cached world transforms
  private _worldPositions!: Float32Array;
  private _worldRotations!: Float32Array;
  private _worldScales!: Float32Array;
  
  // Allocation tracking
  private _capacity: number = 0;
  private _count: number = 0;
  private _freeList: number[] = [];
  private _growFactor: number;
  
  // Dirty tracking for batch update
  private _dirtyList: number[] = [];
  private _needsUpdate: boolean = false;
  
  constructor(config: TransformStorageConfig = {}) {
    const initialCapacity = config.initialCapacity ?? 256;
    this._growFactor = config.growFactor ?? 2;
    
    this.resize(initialCapacity);
  }
  
  // ==========================================================================
  // CAPACITY
  // ==========================================================================
  
  /** Current capacity */
  get capacity(): number {
    return this._capacity;
  }
  
  /** Number of allocated transforms */
  get count(): number {
    return this._count;
  }
  
  /**
   * Resize storage to new capacity
   */
  resize(newCapacity: number): void {
    if (newCapacity <= this._capacity) return;
    
    // Allocate new arrays
    const newPositions = new Float32Array(newCapacity * POS_STRIDE);
    const newRotations = new Float32Array(newCapacity);
    const newScales = new Float32Array(newCapacity * SCALE_STRIDE);
    const newParents = new Int32Array(newCapacity);
    const newFirstChild = new Int32Array(newCapacity);
    const newNextSibling = new Int32Array(newCapacity);
    const newFlags = new Uint8Array(newCapacity);
    const newWorldPositions = new Float32Array(newCapacity * POS_STRIDE);
    const newWorldRotations = new Float32Array(newCapacity);
    const newWorldScales = new Float32Array(newCapacity * SCALE_STRIDE);
    
    // Copy existing data
    if (this._positions) {
      newPositions.set(this._positions);
      newRotations.set(this._rotations);
      newScales.set(this._scales);
      newParents.set(this._parents);
      newFirstChild.set(this._firstChild);
      newNextSibling.set(this._nextSibling);
      newFlags.set(this._flags);
      newWorldPositions.set(this._worldPositions);
      newWorldRotations.set(this._worldRotations);
      newWorldScales.set(this._worldScales);
    }
    
    // Initialize new slots
    newParents.fill(-1, this._capacity);
    newFirstChild.fill(-1, this._capacity);
    newNextSibling.fill(-1, this._capacity);
    
    // Default scale to 1,1
    for (let i = this._capacity; i < newCapacity; i++) {
      newScales[i * SCALE_STRIDE] = 1;
      newScales[i * SCALE_STRIDE + 1] = 1;
      newWorldScales[i * SCALE_STRIDE] = 1;
      newWorldScales[i * SCALE_STRIDE + 1] = 1;
    }
    
    // Update references
    this._positions = newPositions;
    this._rotations = newRotations;
    this._scales = newScales;
    this._parents = newParents;
    this._firstChild = newFirstChild;
    this._nextSibling = newNextSibling;
    this._flags = newFlags;
    this._worldPositions = newWorldPositions;
    this._worldRotations = newWorldRotations;
    this._worldScales = newWorldScales;
    
    this._capacity = newCapacity;
  }
  
  // ==========================================================================
  // ALLOCATION
  // ==========================================================================
  
  /**
   * Allocate a new transform slot
   */
  allocate(): TransformHandle {
    // Try free list first
    if (this._freeList.length > 0) {
      const handle = this._freeList.pop()!;
      this.initSlot(handle);
      this._count++;
      return handle;
    }
    
    // Grow if needed
    if (this._count >= this._capacity) {
      this.resize(Math.ceil(this._capacity * this._growFactor));
    }
    
    const handle = this._count;
    this.initSlot(handle);
    this._count++;
    return handle;
  }
  
  /**
   * Free a transform slot
   */
  free(handle: TransformHandle): void {
    if (!this.isValid(handle)) return;
    
    // Remove from parent
    this.setParent(handle, INVALID_HANDLE);
    
    // Orphan all children
    let child = get(this._firstChild, handle);
    while (child !== -1) {
      const next = get(this._nextSibling, child);
      this._parents[child] = -1;
      this.markDirty(child);
      child = next;
    }
    
    // Clear slot
    this._firstChild[handle] = -1;
    this._nextSibling[handle] = -1;
    this._flags[handle] = 0;
    
    // Add to free list
    this._freeList.push(handle);
    this._count--;
  }
  
  /**
   * Check if handle is valid
   */
  isValid(handle: TransformHandle): boolean {
    return handle >= 0 && handle < this._capacity && !this._freeList.includes(handle);
  }
  
  private initSlot(handle: TransformHandle): void {
    const posIdx = handle * POS_STRIDE;
    const scaleIdx = handle * SCALE_STRIDE;
    
    // Position = 0,0,0
    this._positions[posIdx] = 0;
    this._positions[posIdx + 1] = 0;
    this._positions[posIdx + 2] = 0;
    
    // Rotation = 0
    this._rotations[handle] = 0;
    
    // Scale = 1,1
    this._scales[scaleIdx] = 1;
    this._scales[scaleIdx + 1] = 1;
    
    // No parent, no children
    this._parents[handle] = -1;
    this._firstChild[handle] = -1;
    this._nextSibling[handle] = -1;
    
    // Mark dirty
    this._flags[handle] = DIRTY_LOCAL | DIRTY_WORLD;
    this.markDirty(handle);
  }
  
  // ==========================================================================
  // LOCAL TRANSFORM
  // ==========================================================================
  
  // Position
  getX(handle: TransformHandle): number {
    return get(this._positions, handle * POS_STRIDE);
  }

  getY(handle: TransformHandle): number {
    return get(this._positions, handle * POS_STRIDE + 1);
  }

  getZ(handle: TransformHandle): number {
    return get(this._positions, handle * POS_STRIDE + 2);
  }
  
  setX(handle: TransformHandle, value: number): void {
    this._positions[handle * POS_STRIDE] = value;
    this.markDirty(handle);
  }
  
  setY(handle: TransformHandle, value: number): void {
    this._positions[handle * POS_STRIDE + 1] = value;
    this.markDirty(handle);
  }
  
  setZ(handle: TransformHandle, value: number): void {
    this._positions[handle * POS_STRIDE + 2] = value;
    this.markDirty(handle);
  }
  
  setPosition(handle: TransformHandle, x: number, y: number, z: number = 0): void {
    const idx = handle * POS_STRIDE;
    this._positions[idx] = x;
    this._positions[idx + 1] = y;
    this._positions[idx + 2] = z;
    this.markDirty(handle);
  }
  
  // Rotation
  getRotation(handle: TransformHandle): number {
    return get(this._rotations, handle);
  }
  
  setRotation(handle: TransformHandle, radians: number): void {
    this._rotations[handle] = radians;
    this.markDirty(handle);
  }
  
  // Scale
  getScaleX(handle: TransformHandle): number {
    return get(this._scales, handle * SCALE_STRIDE);
  }

  getScaleY(handle: TransformHandle): number {
    return get(this._scales, handle * SCALE_STRIDE + 1);
  }
  
  setScaleX(handle: TransformHandle, value: number): void {
    this._scales[handle * SCALE_STRIDE] = value;
    this.markDirty(handle);
  }
  
  setScaleY(handle: TransformHandle, value: number): void {
    this._scales[handle * SCALE_STRIDE + 1] = value;
    this.markDirty(handle);
  }
  
  setScale(handle: TransformHandle, x: number, y: number): void {
    const idx = handle * SCALE_STRIDE;
    this._scales[idx] = x;
    this._scales[idx + 1] = y;
    this.markDirty(handle);
  }
  
  // ==========================================================================
  // HIERARCHY
  // ==========================================================================
  
  /**
   * Get parent handle
   */
  getParent(handle: TransformHandle): TransformHandle {
    return get(this._parents, handle);
  }
  
  /**
   * Set parent (handles hierarchy updates)
   */
  setParent(handle: TransformHandle, parent: TransformHandle): void {
    const oldParent = get(this._parents, handle);
    if (oldParent === parent) return;
    
    // Remove from old parent's children
    if (oldParent !== -1) {
      this.removeFromSiblings(handle, oldParent);
    }
    
    // Add to new parent's children
    this._parents[handle] = parent;
    if (parent !== -1) {
      this.addToSiblings(handle, parent);
    }
    
    // Mark dirty (world transform changed)
    this.markDirty(handle);
  }
  
  /**
   * Get first child handle
   */
  getFirstChild(handle: TransformHandle): TransformHandle {
    return get(this._firstChild, handle);
  }

  /**
   * Get next sibling handle
   */
  getNextSibling(handle: TransformHandle): TransformHandle {
    return get(this._nextSibling, handle);
  }
  
  /**
   * Check if has children
   */
  hasChildren(handle: TransformHandle): boolean {
    return get(this._firstChild, handle) !== -1;
  }
  
  /**
   * Get child count
   */
  getChildCount(handle: TransformHandle): number {
    let count = 0;
    let child = get(this._firstChild, handle);
    while (child !== -1) {
      count++;
      child = get(this._nextSibling, child);
    }
    return count;
  }
  
  /**
   * Iterate children
   */
  forEachChild(handle: TransformHandle, callback: (child: TransformHandle) => void): void {
    let child = get(this._firstChild, handle);
    while (child !== -1) {
      callback(child);
      child = get(this._nextSibling, child);
    }
  }
  
  private removeFromSiblings(handle: TransformHandle, parent: TransformHandle): void {
    const first = get(this._firstChild, parent);
    
    if (first === handle) {
      // Removing first child
      this._firstChild[parent] = get(this._nextSibling, handle);
    } else {
      // Find previous sibling
      let prev = first;
      while (prev !== -1 && get(this._nextSibling, prev) !== handle) {
        prev = get(this._nextSibling, prev);
      }
      if (prev !== -1) {
        this._nextSibling[prev] = get(this._nextSibling, handle);
      }
    }
    
    this._nextSibling[handle] = -1;
  }
  
  private addToSiblings(handle: TransformHandle, parent: TransformHandle): void {
    // Add as first child (O(1))
    this._nextSibling[handle] = get(this._firstChild, parent);
    this._firstChild[parent] = handle;
  }
  
  // ==========================================================================
  // WORLD TRANSFORM
  // ==========================================================================
  
  /**
   * Get world X position (lazy calculated)
   */
  getWorldX(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldPositions, handle * POS_STRIDE);
  }
  
  /**
   * Get world Y position (lazy calculated)
   */
  getWorldY(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldPositions, handle * POS_STRIDE + 1);
  }
  
  /**
   * Get world Z position (lazy calculated)
   */
  getWorldZ(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldPositions, handle * POS_STRIDE + 2);
  }
  
  /**
   * Get world rotation (lazy calculated)
   */
  getWorldRotation(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldRotations, handle);
  }
  
  /**
   * Get world scale X (lazy calculated)
   */
  getWorldScaleX(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldScales, handle * SCALE_STRIDE);
  }
  
  /**
   * Get world scale Y (lazy calculated)
   */
  getWorldScaleY(handle: TransformHandle): number {
    this.ensureWorldUpToDate(handle);
    return get(this._worldScales, handle * SCALE_STRIDE + 1);
  }
  
  /**
   * Get full world transform
   */
  getWorldTransform(handle: TransformHandle): WorldTransform {
    this.ensureWorldUpToDate(handle);
    const posIdx = handle * POS_STRIDE;
    const scaleIdx = handle * SCALE_STRIDE;
    return {
      x: get(this._worldPositions, posIdx),
      y: get(this._worldPositions, posIdx + 1),
      z: get(this._worldPositions, posIdx + 2),
      rotation: get(this._worldRotations, handle),
      scaleX: get(this._worldScales, scaleIdx),
      scaleY: get(this._worldScales, scaleIdx + 1),
    };
  }
  
  /**
   * Set world position (converts to local)
   */
  setWorldPosition(handle: TransformHandle, x: number, y: number, z: number = 0): void {
    const parent = get(this._parents, handle);
    if (parent === -1) {
      this.setPosition(handle, x, y, z);
    } else {
      // Convert world to local
      this.ensureWorldUpToDate(parent);
      const parentWorldX = get(this._worldPositions, parent * POS_STRIDE);
      const parentWorldY = get(this._worldPositions, parent * POS_STRIDE + 1);
      const parentWorldZ = get(this._worldPositions, parent * POS_STRIDE + 2);
      
      this.setPosition(handle, x - parentWorldX, y - parentWorldY, z - parentWorldZ);
    }
  }
  
  // ==========================================================================
  // DIRTY TRACKING & UPDATE
  // ==========================================================================
  
  private markDirty(handle: TransformHandle): void {
    if (get(this._flags, handle) & DIRTY_WORLD) return; // Already dirty
    
    this._flags[handle] |= DIRTY_WORLD;
    this._dirtyList.push(handle);
    this._needsUpdate = true;
    
    // Propagate to children
    this.markChildrenDirty(handle);
  }
  
  private markChildrenDirty(handle: TransformHandle): void {
    let child = get(this._firstChild, handle);
    while (child !== -1) {
      if (!(get(this._flags, child) & DIRTY_WORLD)) {
        this._flags[child] |= DIRTY_WORLD;
        this._dirtyList.push(child);
        this.markChildrenDirty(child);
      }
      child = get(this._nextSibling, child);
    }
  }
  
  /**
   * Ensure world transform is up to date for a single handle
   */
  private ensureWorldUpToDate(handle: TransformHandle): void {
    if (!(get(this._flags, handle) & DIRTY_WORLD)) return;
    
    // Update parent first (recursive)
    const parent = get(this._parents, handle);
    if (parent !== -1) {
      this.ensureWorldUpToDate(parent);
    }
    
    this.updateWorldTransform(handle);
  }
  
  private updateWorldTransform(handle: TransformHandle): void {
    const posIdx = handle * POS_STRIDE;
    const scaleIdx = handle * SCALE_STRIDE;
    const parent = get(this._parents, handle);
    
    if (parent === -1) {
      // Root - world = local
      this._worldPositions[posIdx] = get(this._positions, posIdx);
      this._worldPositions[posIdx + 1] = get(this._positions, posIdx + 1);
      this._worldPositions[posIdx + 2] = get(this._positions, posIdx + 2);
      this._worldRotations[handle] = get(this._rotations, handle);
      this._worldScales[scaleIdx] = get(this._scales, scaleIdx);
      this._worldScales[scaleIdx + 1] = get(this._scales, scaleIdx + 1);
    } else {
      // Child - compose with parent
      const parentPosIdx = parent * POS_STRIDE;
      const parentScaleIdx = parent * SCALE_STRIDE;
      
      // Simple composition (no rotation of position yet - TODO)
      this._worldPositions[posIdx] = get(this._worldPositions, parentPosIdx) + get(this._positions, posIdx);
      this._worldPositions[posIdx + 1] = get(this._worldPositions, parentPosIdx + 1) + get(this._positions, posIdx + 1);
      this._worldPositions[posIdx + 2] = get(this._worldPositions, parentPosIdx + 2) + get(this._positions, posIdx + 2);
      this._worldRotations[handle] = get(this._worldRotations, parent) + get(this._rotations, handle);
      this._worldScales[scaleIdx] = get(this._worldScales, parentScaleIdx) * get(this._scales, scaleIdx);
      this._worldScales[scaleIdx + 1] = get(this._worldScales, parentScaleIdx + 1) * get(this._scales, scaleIdx + 1);
    }
    
    // Clear dirty flag
    this._flags[handle] &= ~DIRTY_WORLD;
  }
  
  /**
   * Batch update all dirty world transforms
   * Call this once per frame for best performance
   */
  updateWorldTransforms(): void {
    if (!this._needsUpdate) return;
    
    // Process dirty list
    for (const handle of this._dirtyList) {
      if (get(this._flags, handle) & DIRTY_WORLD) {
        this.ensureWorldUpToDate(handle);
      }
    }
    
    this._dirtyList.length = 0;
    this._needsUpdate = false;
  }
  
  // ==========================================================================
  // BULK OPERATIONS
  // ==========================================================================
  
  /**
   * Get raw positions array for GPU upload
   */
  getPositionsBuffer(): Float32Array {
    return this._positions;
  }
  
  /**
   * Get raw world positions array
   */
  getWorldPositionsBuffer(): Float32Array {
    this.updateWorldTransforms();
    return this._worldPositions;
  }
  
  /**
   * Get raw rotations array
   */
  getRotationsBuffer(): Float32Array {
    return this._rotations;
  }
  
  /**
   * Get raw world rotations array
   */
  getWorldRotationsBuffer(): Float32Array {
    this.updateWorldTransforms();
    return this._worldRotations;
  }
  
  // ==========================================================================
  // STATE SAVE/RESTORE
  // ==========================================================================
  
  /**
   * Save current state (for play mode)
   */
  saveState(): TransformStorageSnapshot {
    return {
      positions: new Float32Array(this._positions),
      rotations: new Float32Array(this._rotations),
      scales: new Float32Array(this._scales),
      parents: new Int32Array(this._parents),
      firstChild: new Int32Array(this._firstChild),
      nextSibling: new Int32Array(this._nextSibling),
      count: this._count,
      freeList: [...this._freeList],
    };
  }
  
  /**
   * Restore state from snapshot
   */
  restoreState(snapshot: TransformStorageSnapshot): void {
    // Ensure capacity
    if (snapshot.positions.length > this._positions.length) {
      this.resize(snapshot.positions.length / POS_STRIDE);
    }
    
    // Restore arrays
    this._positions.set(snapshot.positions);
    this._rotations.set(snapshot.rotations);
    this._scales.set(snapshot.scales);
    this._parents.set(snapshot.parents);
    this._firstChild.set(snapshot.firstChild);
    this._nextSibling.set(snapshot.nextSibling);
    this._count = snapshot.count;
    this._freeList = [...snapshot.freeList];
    
    // Mark all as dirty to recalculate world transforms
    this._flags.fill(DIRTY_WORLD);
    this._dirtyList.length = 0;
    for (let i = 0; i < this._count; i++) {
      if (!this._freeList.includes(i)) {
        this._dirtyList.push(i);
      }
    }
    this._needsUpdate = true;
  }
  
  /**
   * Clear all transforms
   */
  clear(): void {
    this._count = 0;
    this._freeList.length = 0;
    this._dirtyList.length = 0;
    this._needsUpdate = false;
    this._parents.fill(-1);
    this._firstChild.fill(-1);
    this._nextSibling.fill(-1);
    this._flags.fill(0);
  }
}

/**
 * Snapshot for save/restore
 */
export interface TransformStorageSnapshot {
  positions: Float32Array;
  rotations: Float32Array;
  scales: Float32Array;
  parents: Int32Array;
  firstChild: Int32Array;
  nextSibling: Int32Array;
  count: number;
  freeList: number[];
}

/**
 * Global transform storage instance
 */
let globalStorage: TransformStorage | null = null;

/**
 * Get global transform storage
 */
export function getTransformStorage(): TransformStorage {
  if (!globalStorage) {
    globalStorage = new TransformStorage({ initialCapacity: 1024 });
  }
  return globalStorage;
}

/**
 * Reset global transform storage (for testing)
 */
export function resetTransformStorage(): void {
  globalStorage = null;
}
